/**
 * Regras puras de permissão de localização do app do prestador.
 * Diferencia permissão negada, GPS indisponível e tempo esgotado, e escolhe
 * o guia de liberação conforme o aparelho/navegador.
 *
 * RELEASE: 2026-10-21 v1.0.0 - Versão inicial (guia de permissão em vez de erro).
 */

export type GeoFailure = 'denied' | 'unavailable' | 'timeout';
export type GeoPlatform = 'ios_safari' | 'ios_chrome' | 'android' | 'other';

/** Converte o código do GeolocationPositionError (1, 2, 3) no motivo exibido. */
export function classifyGeoError(code: number): GeoFailure {
  if (code === 1) return 'denied';
  if (code === 3) return 'timeout';
  return 'unavailable';
}

/** Detecta a plataforma pelo userAgent para mostrar os passos corretos. */
export function detectPlatform(userAgent: string): GeoPlatform {
  const ua = userAgent || '';
  const ios = /iPhone|iPad|iPod/i.test(ua);
  if (ios) return /CriOS|FxiOS|EdgiOS/i.test(ua) ? 'ios_chrome' : 'ios_safari';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

/** Passos de liberação por plataforma (texto exibido ao prestador). */
export function permissionSteps(platform: GeoPlatform): string[] {
  switch (platform) {
    case 'ios_safari':
      return [
        'Toque em "Aa" na barra de endereço → Ajustes do Site → Localização → Permitir.',
        'Se não resolver: Ajustes do iPhone → Privacidade e Segurança → Serviços de Localização (ligado) → Safari Websites → "Ao Usar o App".',
        'Volte aqui e toque em "Tentar novamente".',
      ];
    case 'ios_chrome':
      return [
        'Abra os Ajustes do iPhone → Privacidade e Segurança → Serviços de Localização (ligado).',
        'Nos Ajustes do iPhone, abra Chrome → Localização → "Ao Usar o App".',
        'Volte aqui e toque em "Tentar novamente".',
      ];
    case 'android':
      return [
        'Toque no cadeado ao lado do endereço → Permissões → Localização → Permitir.',
        'Confira se a Localização (GPS) do aparelho está ligada.',
        'Volte aqui e toque em "Tentar novamente".',
      ];
    default:
      return [
        'Clique no cadeado ao lado do endereço do site e permita a Localização.',
        'Confira se a localização do aparelho está ligada.',
        'Volte aqui e tente novamente.',
      ];
  }
}

/** Consulta o estado atual da permissão (quando o navegador suporta). */
export async function queryGeoPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unknown'> {
  try {
    const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
    if (!perms?.query) return 'unknown';
    const status = await perms.query({ name: 'geolocation' as PermissionName });
    return status.state as 'granted' | 'denied' | 'prompt';
  } catch {
    return 'unknown';
  }
}
